import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

/**
 * Site-wide ambient starfield. Sits behind all content (fixed, pointer-events
 * none) and acts as the app's background texture.
 *
 * - Everywhere: a gentle field of faint gold stars that softly float + twinkle.
 * - On the Knowledge Universe (/universe): TRIPLE density arranged in three
 *   depth layers (far / mid / near) with warmer star colors, for an immersive
 *   "deep space" star map behind the graph.
 * - An occasional shooting star (~every 30s).
 *
 * Respects prefers-reduced-motion via CSS (see index.css).
 */
const BASE_COUNT = 240;
const SHOOT_INTERVAL_MS = 30000;

// size / opacity ranges per depth layer [far, mid, near]
const LAYER_SIZE = [[0.5, 1.1], [0.9, 1.7], [1.3, 2.4]];
const LAYER_OP = [[0.14, 0.4], [0.22, 0.55], [0.3, 0.82]];
const LAYER_FLOAT = [9, 7, 5]; // near layer drifts a touch faster (depth cue)
const rand = (a, b) => a + Math.random() * (b - a);

function makeStars(count, layered) {
  return Array.from({ length: count }).map((_, i) => {
    let layer = 0;
    if (layered) {
      const r = Math.random();
      layer = r < 0.55 ? 0 : r < 0.85 ? 1 : 2;
    }
    const [smin, smax] = layered ? LAYER_SIZE[layer] : [1, 3.2];
    const [omin, omax] = layered ? LAYER_OP[layer] : [0.12, 0.52];
    const size = rand(smin, smax);
    let color = "#D8B66A";
    if (layered) {
      const c = Math.random();
      color = c < 0.7 ? "#F4E8C8" : c < 0.9 ? "#F4DE9F" : "#D8B66A";
    }
    return {
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size,
      op: rand(omin, omax),
      color,
      glow: size * 2.6,
      // every star twinkles for a livelier sky
      twinkle: true,
      floatDur: (layered ? LAYER_FLOAT[layer] : 6) + Math.random() * 7,
      floatDelay: Math.random() * 6,
      twDur: 1.8 + Math.random() * (layered ? 8 : 3.4),
      twDelay: Math.random() * 5,
    };
  });
}

function randomShot() {
  return {
    left: 4 + Math.random() * 46,
    top: 3 + Math.random() * 40,
    angle: 16 + Math.random() * 22,
    dist: 240 + Math.random() * 200,
  };
}

export const Starfield = () => {
  const onUniverse = useLocation().pathname === "/universe";
  const count = onUniverse ? 860 : BASE_COUNT;
  const stars = useMemo(() => makeStars(count, onUniverse), [count, onUniverse]);
  const glowRgba = onUniverse ? "rgba(244,232,200,0.5)" : "rgba(216,182,106,0.55)";
  const [shot, setShot] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fire = () => {
      if (cancelled) return;
      setShot({ ...randomShot(), key: Date.now() });
    };
    const initial = setTimeout(fire, 4000);
    const interval = setInterval(fire, SHOOT_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, []);

  return (
    <div className="codex-starfield" aria-hidden data-testid={onUniverse ? "universe-starfield" : "starfield"}>
      {stars.map((d) => (
        <span
          key={d.id}
          className="codex-star absolute rounded-full"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: d.size,
            height: d.size,
            backgroundColor: d.color,
            opacity: d.op,
            "--tw": d.op,
            boxShadow: `0 0 ${d.glow}px ${glowRgba}`,
            animation: d.twinkle
              ? `codex-star-float ${d.floatDur}s ease-in-out ${d.floatDelay}s infinite, codex-star-twinkle ${d.twDur}s ease-in-out ${d.twDelay}s infinite`
              : `codex-star-float ${d.floatDur}s ease-in-out ${d.floatDelay}s infinite`,
          }}
        />
      ))}

      {shot ? (
        <span
          key={shot.key}
          className="codex-shooting-star"
          style={{
            left: `${shot.left}%`,
            top: `${shot.top}%`,
            "--ang": `${shot.angle}deg`,
            "--dist": `${shot.dist}px`,
          }}
        />
      ) : null}
    </div>
  );
};

export default Starfield;
